import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { progress, useProgress } from "../lib/progress";
import { hrefFor, navigate } from "../lib/router";
import CodeWorkspace from "./CodeWorkspace";
import { ChevronLeftIcon, ChevronRightIcon } from "./Icons";
import Instructions from "./Instructions";
import LevelSidebar from "./LevelSidebar";
import QuizWorkspace from "./QuizWorkspace";

function LevelPage({ track, located, theme, sidebarOpen, onCloseSidebar }) {
  const { t } = useTranslation();
  const { level, chapter, index, total, previous, next } = located;
  const { solved } = useProgress();
  const isSolved = Boolean(solved[level.id]);

  const [tab, setTab] = useState(isSolved ? "review" : "lesson");
  const [code, setCode] = useState(() => progress.get().code[level.id] ?? level.starter ?? "");

  useEffect(() => {
    progress.setLastLevel(track.id, level.id);
  }, [track.id, level.id]);

  const changeCode = (value) => {
    setCode(value);
    if (value === level.starter) progress.clearCode(level.id);
    else progress.saveCode(level.id, value);
  };

  const onSolved = useCallback(() => progress.markSolved(level.id), [level.id]);
  const goNext = () => next && navigate(track.id, next.id);

  const footer = (
    <nav className="level-nav" aria-label={t("level.position", { index: index + 1, total })}>
      {previous ? (
        <a className="button button--ghost button--small" href={hrefFor(track.id, previous.id)}>
          <ChevronLeftIcon />
          {t("level.previous")}
        </a>
      ) : (
        <span />
      )}
      {next && (
        <a className={`button button--small ${isSolved ? "button--primary" : "button--ghost"}`} href={hrefFor(track.id, next.id)}>
          {t("level.next")}
          <ChevronRightIcon />
        </a>
      )}
    </nav>
  );

  return (
    <div className="level-layout">
      <LevelSidebar track={track} currentLevelId={level.id} open={sidebarOpen} onClose={onCloseSidebar} />
      <main id="main" className={`level-main level-main--${level.type}`}>
        <Instructions
          level={level}
          chapter={chapter}
          position={{ index: index + 1, total }}
          isSolved={isSolved}
          tab={tab}
          onTabChange={setTab}
          onLoadSolution={() => changeCode(level.solution)}
          onHint={() => progress.recordHint(level.id)}
          onReveal={() => progress.recordReveal(level.id)}
          footer={footer}
        />
        {level.type === "code" ? (
          <CodeWorkspace
            level={level}
            code={code}
            onCodeChange={changeCode}
            onReset={() => changeCode(level.starter)}
            onSolved={onSolved}
            onNext={goNext}
            onReview={() => setTab("review")}
            hasNext={Boolean(next)}
            theme={theme}
          />
        ) : (
          <QuizWorkspace level={level} onNext={goNext} hasNext={Boolean(next)} />
        )}
      </main>
    </div>
  );
}

export default LevelPage;
